/**
 * The two data hooks this app needs.
 *
 * WHY NOT TanStack Query: the POS has exactly two access patterns — "fetch once
 * on mount" and "poll this every N seconds until the screen unmounts" — and it
 * must be provably free of background refetches that could re-render a payment
 * screen mid-tap. Sixty lines of explicit code beat a cache whose invalidation
 * rules a reviewer has to reconstruct.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { isApiError } from '@sonauto/api-client';

export interface AsyncState<T> {
  readonly data: T | null;
  readonly error: Error | null;
  readonly loading: boolean;
  /** True only for the FIRST load, so a poll never flashes a spinner. */
  readonly initialLoading: boolean;
  readonly reload: () => void;
}

/** Vietnamese message for anything that can be thrown by the API client. */
export function errorMessage(error: unknown): string {
  if (isApiError(error)) return error.message;
  if (error instanceof Error && error.message !== '') return error.message;
  return 'Đã xảy ra lỗi không xác định.';
}

/**
 * Fetch once, and again whenever `deps` change or `reload()` is called.
 *
 * `enabled: false` is how a screen waits for a prerequisite (a branch id, a
 * station id) without a conditional hook.
 */
export function useAsync<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  options: { enabled?: boolean } = {},
): AsyncState<T> {
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);
  const seenOnce = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    let alive = true;
    setLoading(true);
    fetcherRef
      .current(controller.signal)
      .then((value) => {
        if (!alive) return;
        seenOnce.current = true;
        setData(value);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!alive || controller.signal.aborted) return;
        seenOnce.current = true;
        setError(new Error(errorMessage(cause)));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, nonce, ...deps]);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  return { data, error, loading, initialLoading: loading && !seenOnce.current, reload };
}

/**
 * Poll an endpoint on an interval.
 *
 * Three behaviours that matter on a real floor:
 *  - the timer is paused while the tab is hidden, so a KDS screen locked
 *    overnight does not hammer the Worker for eight hours;
 *  - a poll never clears the previous data on failure — a chef staring at a
 *    board must not lose it because one request timed out;
 *  - the interval restarts after each settle rather than on a fixed schedule,
 *    so a slow response cannot pile requests up.
 *
 * Polling is also the PERMANENT safety net under the BranchHub WebSocket (see
 * lib/realtime.ts): screens that subscribe keep a relaxed poll running while
 * connected and fall back to this cadence the moment the socket drops.
 */
export function usePoll<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  intervalMs: number,
  deps: readonly unknown[],
  options: { enabled?: boolean } = {},
): AsyncState<T> & { readonly lastUpdatedAt: number | null } {
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0);
  const seenOnce = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    let timer: number | undefined;
    const controller = new AbortController();

    const tick = async (): Promise<void> => {
      if (!alive) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        timer = window.setTimeout(() => void tick(), intervalMs);
        return;
      }
      setLoading(true);
      try {
        const value = await fetcherRef.current(controller.signal);
        if (!alive) return;
        seenOnce.current = true;
        setData(value);
        setError(null);
        setLastUpdatedAt(Date.now());
      } catch (cause: unknown) {
        if (alive && !controller.signal.aborted) {
          seenOnce.current = true;
          // Deliberately keep `data` — a stale board beats a blank board.
          setError(new Error(errorMessage(cause)));
        }
      } finally {
        if (alive) {
          setLoading(false);
          timer = window.setTimeout(() => void tick(), intervalMs);
        }
      }
    };

    void tick();
    return () => {
      alive = false;
      controller.abort();
      if (timer !== undefined) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, nonce, ...deps]);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  return { data, error, loading, initialLoading: loading && !seenOnce.current, reload, lastUpdatedAt };
}

/** A ticking clock, for elapsed timers that must move without a refetch. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** `navigator.onLine`, as React state. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const up = (): void => setOnline(true);
    const down = (): void => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}
